import { occBaseSiteUrl, occFetch, occUrl } from "~/app/api/occ/occFetch";
import { pageDataKeys, sessionQueryOptions } from "~/app/api/pageData";
import { queryClient } from "~/app/queryClient";
import { invalidateMinicart } from "~/app/store/bffApi";
import { resolveActiveCartResourceId } from "~/features/cart/cart/utils/cartResourceIdentity";
import { createActiveCart, isCartAlreadyExistsForContext } from "./cartMergeApi";
import {
  normalizeBulkUploadAddResponse,
  normalizeBulkUploadPreviewResponse,
  type BulkUploadAddRequestWsDTO,
  type BulkUploadAddResponse,
  type BulkUploadAddResponseWsDTO,
  type BulkUploadPreviewResponse,
  type BulkUploadPreviewResponseWsDTO,
  type BulkUploadTemplate,
  type BulkUploadTemplateType,
  type BulkUploadTextRequestWsDTO,
  type BulkUploadWorkflowOperations,
} from "../types/contracts";

const BULK_UPLOAD_PATH = "/bulkupload";

export class BulkUploadApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`Bulk Upload request failed with status ${status}`);
    this.name = "BulkUploadApiError";
    this.status = status;
    this.body = body;
  }
}

export async function uploadBulkFile(file: File, signal?: AbortSignal): Promise<BulkUploadPreviewResponse> {
  const body = new FormData();
  body.append("file", file);

  const response = await occFetch(occUrl(`${BULK_UPLOAD_PATH}/upload/file`), {
    body,
    method: "POST",
    signal,
  });

  return normalizeBulkUploadPreviewResponse(await readJsonResponse<BulkUploadPreviewResponseWsDTO>(response));
}

export async function uploadBulkText(csvInputString: string, signal?: AbortSignal): Promise<BulkUploadPreviewResponse> {
  const body: BulkUploadTextRequestWsDTO = { csvInputString };
  const response = await occFetch(occUrl(`${BULK_UPLOAD_PATH}/upload/text`), {
    body: JSON.stringify(body),
    headers: {
      "content-type": "application/json",
    },
    method: "POST",
    signal,
  });

  return normalizeBulkUploadPreviewResponse(await readJsonResponse<BulkUploadPreviewResponseWsDTO>(response));
}

export async function addBulkUploadToCart(
  request: BulkUploadAddRequestWsDTO,
  override: boolean,
  signal?: AbortSignal,
): Promise<BulkUploadAddResponse> {
  const session = await queryClient.fetchQuery(sessionQueryOptions());
  const activeCartCode = session.activeCart?.code;
  if (!activeCartCode) {
    throw new Error("Cannot submit Bulk Upload without an explicit Active Cart identity");
  }

  const response = await occFetch(
    occBaseSiteUrl(`/users/current/carts/${encodeURIComponent(activeCartCode)}/bulk-upload?override=${override}`),
    {
      body: JSON.stringify(request),
      headers: {
        "content-type": "application/json",
      },
      method: "POST",
      signal,
    },
  );

  const result = normalizeBulkUploadAddResponse(await readJsonResponse<BulkUploadAddResponseWsDTO>(response));
  if (result.outcome === "SUCCESS") {
    if (override) {
      await refreshAuthoritativeSession();
    } else {
      invalidateMinicart();
    }
  }
  return result;
}

export async function fetchBulkUploadTemplate(
  type: BulkUploadTemplateType,
  signal?: AbortSignal,
): Promise<BulkUploadTemplate> {
  const response = await occFetch(occUrl(`${BULK_UPLOAD_PATH}/template/${type}`), {
    method: "GET",
    signal,
  });
  await throwIfNotOk(response);

  const contentLengthHeader = response.headers.get("content-length");
  const contentLength = contentLengthHeader ? Number.parseInt(contentLengthHeader, 10) : undefined;

  return {
    blob: await response.blob(),
    fileName: getTemplateFileName(response.headers.get("content-disposition"), type),
    contentType: response.headers.get("content-type") ?? "application/octet-stream",
    ...(contentLength !== undefined && Number.isFinite(contentLength) ? { contentLength } : {}),
  };
}

export type OrdinaryBulkUploadPreviewInput =
  | {
      source: "file";
      file: File;
    }
  | {
      source: "text";
      csvInputString: string;
    };

export interface OrdinaryBulkUploadSubmitInput {
  request: BulkUploadAddRequestWsDTO;
  override: boolean;
}

export interface OrdinaryBulkUploadOperations
  extends BulkUploadWorkflowOperations<"ordinary", OrdinaryBulkUploadPreviewInput, OrdinaryBulkUploadSubmitInput> {
  downloadTemplate(type: BulkUploadTemplateType, signal?: AbortSignal): Promise<BulkUploadTemplate>;
}

export const ordinaryBulkUploadOperations: OrdinaryBulkUploadOperations = {
  mode: "ordinary",
  async preview(input, signal) {
    await ensureOrdinaryBulkUploadTarget(signal);
    return input.source === "file" ? uploadBulkFile(input.file, signal) : uploadBulkText(input.csvInputString, signal);
  },
  submit(input, signal) {
    return addBulkUploadToCart(input.request, input.override, signal);
  },
  downloadTemplate: fetchBulkUploadTemplate,
};

async function ensureOrdinaryBulkUploadTarget(signal?: AbortSignal): Promise<void> {
  if (await resolveActiveCartResourceId()) {
    return;
  }

  try {
    await createActiveCart(signal);
  } catch (error) {
    if (!isCartAlreadyExistsForContext(error)) {
      throw error;
    }
  }

  await queryClient.invalidateQueries({
    exact: true,
    queryKey: pageDataKeys.session(),
    refetchType: "none",
  });
  if (!(await resolveActiveCartResourceId())) {
    throw new Error("Cart creation did not resolve to an explicit Active Cart");
  }
}

async function refreshAuthoritativeSession(): Promise<void> {
  await queryClient.invalidateQueries({
    exact: true,
    queryKey: pageDataKeys.session(),
    refetchType: "none",
  });
  await queryClient.fetchQuery(sessionQueryOptions());
}

async function readJsonResponse<T>(response: Response): Promise<T> {
  const body = await readResponseBody(response);
  if (body === undefined || body === null || typeof body !== "object") {
    throw new BulkUploadApiError(response.status, body);
  }
  if (!response.ok && !isBulkUploadResponseRoot(body)) {
    throw new BulkUploadApiError(response.status, body);
  }
  return body as T;
}

function isBulkUploadResponseRoot(body: object): boolean {
  if (Array.isArray(body) || !("outcome" in body)) {
    return false;
  }
  return BULK_UPLOAD_OUTCOMES.has(body.outcome);
}

const BULK_UPLOAD_OUTCOMES = new Set<unknown>([
  "PREVIEW",
  "UPLOAD_ERROR",
  "VALIDATION_FAILED",
  "CONTEXT_CHANGED",
  "SUCCESS",
  "BUSINESS_ERROR",
  "INVALID_REQUEST",
]);

async function throwIfNotOk(response: Response): Promise<void> {
  if (response.ok) {
    return;
  }
  throw new BulkUploadApiError(response.status, await readResponseBody(response));
}

async function readResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) {
    return undefined;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function getTemplateFileName(contentDisposition: string | null, type: BulkUploadTemplateType): string {
  if (!contentDisposition) {
    return `bulk-upload.${type}`;
  }

  const encodedName = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  if (encodedName) {
    return decodeURIComponent(encodedName);
  }

  return contentDisposition.match(/filename="?([^";]+)"?/i)?.[1] ?? `bulk-upload.${type}`;
}
